import { isValid, parse, differenceInYears, format } from "date-fns";
import Case from "case";
import { derived } from "svelte/store";
import { languageStore } from "../stores/language";

interface DateInput {
  month: number | string;
  day: number | string;
  year: number | string;
}

export interface CheckAgeValidationResult {
  isValid: boolean;
  hasError: boolean;
}

export interface AgeDetails {
  age: number;
  dob: string
}

const MAX_ALLOWED_AGE = 150;
const CURRENT_YEAR = new Date().getFullYear();
const MAX_YEAR = CURRENT_YEAR - 1;
const MIN_YEAR = CURRENT_YEAR - MAX_ALLOWED_AGE;

export const validateDate = ({ month, day, year }: DateInput): boolean => {
  const parsedDate = parse(`${year}-${month}-${day}`, "yyyy-MM-dd", new Date());

  const yearNumber = Number(year);
  const isValidYear = yearNumber >= MIN_YEAR && yearNumber <= MAX_YEAR;
  const isValidDate = isValidYear && isValid(parsedDate);
  return isValidDate;
};

export const calculateAgeDetails = ({ month, day, year }: DateInput): AgeDetails => {
  const parsedDate = parse(`${year}-${month}-${day}`, "yyyy-MM-dd", new Date());
  const age = differenceInYears(new Date(), parsedDate);

  return {
    age,
    dob: format(parsedDate, "yyyy-MM-dd")
  };
};

const getMonthsOptions = (language: string = "en") => {
  const currentYear = new Date().getFullYear();
  const formatter = (date: Date) => new Intl.DateTimeFormat(language, { month: "long" }).format(date);
  const result = [...Array(12).keys()].map((idx) => ({
    value: String(idx + 1),
    label: formatter(new Date(currentYear, idx))
  }));
  return result as Array<{ value: string, label: string }>;
};

export const MonthOptions = derived([languageStore], ([$language]) => getMonthsOptions($language));

const getInputLabels = (language: string = "en") => {
  const displayNames = new Intl.DisplayNames(language, { type: "dateTimeField" });

  return ({
    day: Case.capital(displayNames.of("day") || ""),
    month: Case.capital(displayNames.of("month") || ""),
    year: Case.capital(displayNames.of("year") || "")
  }) as Record<string, string>;
};

export const InputLabels = derived([languageStore], ([$language]) => getInputLabels($language));
